import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { useEffect, useRef, useState } from "react";
import { Fragment, type Node as ProseMirrorNode } from "@tiptap/pm/model";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold as BoldIcon,
  Check,
  Heading2,
  Heading3,
  Italic as ItalicIcon,
  Link as LinkIcon,
  List,
  ListOrdered,
  AlignVerticalSpaceBetween,
  Palette,
  Strikethrough,
  Type,
  Underline,
  X as XIcon,
} from "lucide-react";
import {
  CMS_FONT_SIZE_MAX_PX,
  CMS_FONT_SIZE_MIN_PX,
  CMS_FONT_SIZES_PX,
} from "@/lib/cms/cms-theme.constants";
import { normalizeFontSizeInput } from "@/components/tiptap/extensions/FontSize";
import { listPuckPages } from "@/api/cms-puck/api";
import { useQuery } from "@tanstack/react-query";
import { usePuck } from "@measured/puck";
import type { BlockStyle } from "@/lib/cms-puck/block-style";
import { preserveCanvasScroll } from "@/lib/cms-puck/preserve-canvas-scroll";
import { cn } from "@/lib/utils";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import {
  EMPTY_RICHTEXT_DOC,
  PARAGRAPH_SPACING_DEFAULT_PX,
  PARAGRAPH_SPACING_MAX_PX,
  PARAGRAPH_SPACING_MIN_PX,
  createPuckRichTextExtensions,
  handleRichTextInput,
  markActiveInDoc,
} from "@/lib/cms-puck/contract/richtext-extensions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

/**
 * Éditeur de texte riche UNIQUE des blocs RichText Puck (remplace l'ancien
 * `CmsRichTextInline`). Édité dans la SIDEBAR : le canvas Puck pose `user-select: none`
 * sur ses blocs draggables, ce qui empêche la sélection inline. Toolbar FIXE au-dessus.
 *
 * Mode Code (style WordPress) : textarea HTML source — les balises saisies sont
 * enregistrées telles quelles dans `body` (puis assainies au save API).
 *
 * Les extensions Tiptap (et les contournements Chrome du « gras involontaire ») vivent
 * dans la SOURCE DE VÉRITÉ partagée `@/lib/cms-puck/contract/richtext-extensions`. Émet du
 * HTML (`onChange`) compatible avec le sanitizer API (p/strong/em/u/s/span/a/ul/ol/li).
 */

export type RichTextFeature =
  | "bold"
  | "italic"
  | "underline"
  | "strike"
  | "color"
  | "fontSize"
  | "link"
  | "lists"
  | "heading"
  | "align"
  | "paragraphSpacing";

const ALL_FEATURES: RichTextFeature[] = [
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "fontSize",
  "link",
  "lists",
  "heading",
  "align",
  "paragraphSpacing",
];

function getParagraphSpacingPx(editor: Editor): number {
  const raw = editor.getAttributes("paragraph").paragraphSpacing;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return PARAGRAPH_SPACING_DEFAULT_PX;
  return Math.max(
    PARAGRAPH_SPACING_MIN_PX,
    Math.min(PARAGRAPH_SPACING_MAX_PX, Math.round(n)),
  );
}

const BTN_BASE =
  "flex h-8 w-8 items-center justify-center rounded text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900";
const BTN_ON = "bg-slate-900 text-white hover:bg-slate-900 hover:text-white";

function Btn({
  active,
  label,
  onClick,
  children,
}: {
  active?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active ? true : undefined}
      title={label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(BTN_BASE, active && BTN_ON)}
    >
      {children}
    </button>
  );
}

function ColorPickerBtn({ editor }: { editor: Editor }) {
  const cmsNew = useCmsNew();
  const theme = usePuckTheme();
  const palette = theme?.colors ?? [];
  const currentColor = editor.getAttributes("textStyle").color as
    | string
    | undefined;

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) preserveCanvasScroll();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={cmsNew("cms.new.auto.label.couleur_du_texte", "Couleur du texte")}
          title={cmsNew("cms.new.auto.title.couleur_du_texte", "Couleur du texte")}
          onMouseDown={(e) => e.preventDefault()}
          className={cn(
            BTN_BASE,
            "relative w-9",
            currentColor && BTN_ON,
          )}
        >
          <Palette className="h-4 w-4" />
          {currentColor ? (
            <span
              className="absolute bottom-1 left-1/2 h-1 w-4 -translate-x-1/2 rounded-full"
              style={{ backgroundColor: currentColor }}
            />
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48 p-2">
        <div className="grid grid-cols-4 gap-1.5">
          {palette.map((color) => (
            <DropdownMenuItem
              key={color.id}
              className="flex h-8 w-8 items-center justify-center p-0"
              title={color.name}
              onClick={() =>
                editor.chain().focus().setFontColor(color.hex).run()
              }
            >
              <span
                className="h-6 w-6 rounded border border-slate-200"
                style={{ backgroundColor: color.hex }}
              />
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuItem
          className="mt-1 text-xs text-slate-500"
          onClick={() => editor.chain().focus().unsetFontColor().run()}
        >
          {cmsNew("cms.new.fields.richText.defaultColor", "Couleur par défaut")}
        </DropdownMenuItem>
        <div className="mt-2 border-t border-slate-100 pt-2">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="color"
              className="h-7 w-10 cursor-pointer rounded border border-slate-200 bg-transparent"
              value={currentColor ?? "#171717"}
              onChange={(e) =>
                editor.chain().focus().setFontColor(e.target.value).run()
              }
            />
            {cmsNew("cms.new.fields.richText.custom", "Personnalisée")}
          </label>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Taille de base d'un paragraphe quand ni le bloc ni le texte n'en posent (= `body` du site). */
const DEFAULT_BASE_FONT_SIZE_PX = 16;

/**
 * Taille des titres du contenu riche, MIROIR de `.cms-html-content h2/h3`
 * (contrat de rendu partagé : 1.5rem / 1.25rem, indépendantes de la taille du bloc).
 */
const HEADING_FONT_SIZE_PX: Record<number, number> = { 2: 24, 3: 20 };

/**
 * Taille de texte EFFECTIVE à la sélection : la taille posée explicitement
 * (`textStyle.fontSize`) sinon celle que le texte reçoit réellement au rendu
 * (titre H2/H3, sinon taille de base du bloc). `explicit` distingue les deux pour
 * n'activer le bouton que lorsqu'une taille a vraiment été choisie.
 */
function getEffectiveFontSize(
  editor: Editor,
  baseFontSizePx: number,
): { px: number; explicit: boolean } {
  const stored = editor.getAttributes("textStyle").fontSize as
    | string
    | null
    | undefined;
  const storedPx = stored ? parseInt(stored, 10) : NaN;
  if (Number.isFinite(storedPx)) return { px: storedPx, explicit: true };

  const parent = editor.state.selection.$from.parent;
  const headingPx =
    parent.type.name === "heading"
      ? HEADING_FONT_SIZE_PX[parent.attrs.level as number]
      : undefined;
  return { px: headingPx ?? baseFontSizePx, explicit: false };
}

function FontSizeBtn({
  editor,
  baseFontSizePx,
}: {
  editor: Editor;
  baseFontSizePx: number;
}) {
  const cmsNew = useCmsNew();
  const { px: currentPx, explicit: hasExplicitSize } = getEffectiveFontSize(
    editor,
    baseFontSizePx,
  );
  const [draft, setDraft] = useState(String(currentPx));

  useEffect(() => {
    setDraft(String(currentPx));
  }, [currentPx]);

  const applyCustomSize = () => {
    const normalized = normalizeFontSizeInput(draft);
    if (!normalized) return;
    // Valeur déjà celle du texte (ex. champ pré-rempli puis simple perte de focus) :
    // ne pas figer une taille explicite que l'utilisateur n'a pas choisie.
    if (parseInt(normalized, 10) === currentPx) return;
    editor.chain().focus().setFontSize(normalized).run();
    setDraft(String(parseInt(normalized, 10)));
  };

  const customLabel = cmsNew(
    "cms.new.fields.richText.customSize",
    "Taille personnalisée",
  );

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          preserveCanvasScroll();
          setDraft(String(currentPx));
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={cmsNew("cms.new.auto.label.taille_du_texte", "Taille du texte")}
          title={cmsNew("cms.new.auto.title.taille_du_texte", "Taille du texte")}
          onMouseDown={(e) => e.preventDefault()}
          className={cn(
            BTN_BASE,
            "w-auto gap-1 px-1.5",
            hasExplicitSize && BTN_ON,
          )}
        >
          <Type className="h-4 w-4" />
          <span className="text-xs tabular-nums">{currentPx}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[9rem] p-1">
        <div
          className="mb-1 flex items-center gap-1 border-b border-slate-100 px-2 pb-1.5 pt-1"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            type="number"
            inputMode="numeric"
            min={CMS_FONT_SIZE_MIN_PX}
            max={CMS_FONT_SIZE_MAX_PX}
            step={1}
            aria-label={customLabel}
            title={customLabel}
            placeholder="px"
            value={draft}
            className="h-7 w-14 rounded border border-slate-200 bg-white px-1.5 text-xs tabular-nums text-slate-700 outline-none focus:border-slate-400"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                applyCustomSize();
              }
            }}
            onBlur={applyCustomSize}
          />
          <span className="text-[11px] text-slate-400">px</span>
        </div>
        {CMS_FONT_SIZES_PX.map((size) => (
          <DropdownMenuItem
            key={size}
            className={cn(
              "justify-between text-sm",
              parseInt(size, 10) === currentPx && "font-semibold text-slate-900",
            )}
            onClick={() => editor.chain().focus().setFontSize(size).run()}
          >
            <span>{parseInt(size, 10)} px</span>
            {parseInt(size, 10) === currentPx ? (
              <Check className="h-3.5 w-3.5" />
            ) : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem
          className="mt-1 border-t border-slate-100 pt-1.5 text-xs text-slate-500"
          onClick={() => editor.chain().focus().unsetFontSize().run()}
        >
          {cmsNew("cms.new.fields.richText.defaultSize", "Taille par défaut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Convertit les `<br>` internes (lignes « soft ») en vrais `<p>`.
 * L'input d'espacement pose `margin-bottom` sur chaque paragraphe : sans ce
 * split, une légende `ligne1<br>ligne2<br>…` reste un seul `<p>` et l'écart
 * n'apparaît qu'après le bloc (cas constaté dans le panneau Contenu).
 */
function normalizeHardBreaksToParagraphs(editor: Editor): boolean {
  const { state, view } = editor;
  const paragraphType = state.schema.nodes.paragraph;
  const hardBreakType = state.schema.nodes.hardBreak;
  if (!paragraphType || !hardBreakType) return false;

  type Replacement = { from: number; to: number; nodes: ProseMirrorNode[] };
  const replacements: Replacement[] = [];

  state.doc.descendants((node, pos) => {
    if (node.type !== paragraphType) return;
    let hasBreak = false;
    node.forEach((child) => {
      if (child.type === hardBreakType) hasBreak = true;
    });
    if (!hasBreak) return;

    const lines: ProseMirrorNode[][] = [[]];
    node.forEach((child) => {
      if (child.type === hardBreakType) {
        lines.push([]);
      } else {
        lines[lines.length - 1].push(child);
      }
    });

    const paragraphs = lines.map((inlines) =>
      paragraphType.create(
        { ...node.attrs },
        inlines.length > 0 ? inlines : undefined,
      ),
    );
    replacements.push({
      from: pos,
      to: pos + node.nodeSize,
      nodes: paragraphs,
    });
  });

  if (replacements.length === 0) return false;

  let tr = state.tr;
  for (let i = replacements.length - 1; i >= 0; i -= 1) {
    const { from, to, nodes } = replacements[i];
    tr = tr.replaceWith(from, to, Fragment.from(nodes));
  }
  try {
    tr = tr.setSelection(state.selection.map(tr.doc, tr.mapping));
  } catch {
    // sélection devenue invalide après un gros replace — on laisse TipTap recalculer
  }
  view.dispatch(tr);
  return true;
}

/**
 * Applique `margin-bottom` à tous les paragraphes du document, sans voler le
 * focus (prévisualisation live dans TipTap + canvas Puck pendant la saisie).
 * Normalise d'abord les soft breaks → paragraphes pour que l'écart soit visible
 * entre les lignes.
 */
function applyParagraphSpacingToDoc(editor: Editor, px: number) {
  normalizeHardBreaksToParagraphs(editor);
  const { state, view } = editor;
  let tr = state.tr;
  let modified = false;
  state.doc.descendants((node, pos) => {
    if (node.type.name !== "paragraph") return;
    if (node.attrs.paragraphSpacing === px) return;
    tr = tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      paragraphSpacing: px,
    });
    modified = true;
  });
  if (!modified) return;
  try {
    tr = tr.setSelection(state.selection.map(tr.doc, tr.mapping));
  } catch {
    // ignore
  }
  view.dispatch(tr);
}

function clampSpacingDraft(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "-") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  return Math.max(
    PARAGRAPH_SPACING_MIN_PX,
    Math.min(PARAGRAPH_SPACING_MAX_PX, Math.round(n)),
  );
}

/** Espacement entre paragraphes — input libre en px (`margin-bottom`), live. */
function ParagraphSpacingInput({ editor }: { editor: Editor }) {
  const cmsNew = useCmsNew();
  const current = getParagraphSpacingPx(editor);
  const [draft, setDraft] = useState(String(current));
  const inputFocusedRef = useRef(false);
  const label = cmsNew(
    "cms.new.fields.richText.paragraphSpacing",
    "Espacement des paragraphes",
  );

  useEffect(() => {
    // Ne pas écraser la saisie en cours (ex. champ vidé avant de taper « 8 »).
    if (inputFocusedRef.current) return;
    setDraft(String(current));
  }, [current]);

  const applyLive = (raw: string) => {
    const px = clampSpacingDraft(raw);
    if (px === null) return;
    applyParagraphSpacingToDoc(editor, px);
  };

  const commit = () => {
    // Vide / invalide → 0 (pas de plancher forcé à 12).
    const px = clampSpacingDraft(draft) ?? PARAGRAPH_SPACING_MIN_PX;
    setDraft(String(px));
    applyParagraphSpacingToDoc(editor, px);
  };

  return (
    <div
      className="flex h-8 items-center gap-1 rounded px-1.5 text-slate-600"
      title={label}
    >
      <AlignVerticalSpaceBetween className="h-4 w-4 shrink-0" aria-hidden />
      <input
        type="number"
        inputMode="numeric"
        min={PARAGRAPH_SPACING_MIN_PX}
        max={PARAGRAPH_SPACING_MAX_PX}
        step={1}
        aria-label={label}
        value={draft}
        onMouseDown={(e) => {
          // stopPropagation : évite que Puck/toolbar traite le clic.
          // Pas de preventDefault : l'input doit pouvoir prendre le focus.
          e.stopPropagation();
        }}
        onFocus={() => {
          inputFocusedRef.current = true;
        }}
        onChange={(e) => {
          const next = e.target.value;
          setDraft(next);
          applyLive(next);
        }}
        onBlur={() => {
          inputFocusedRef.current = false;
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
        }}
        className="h-6 w-12 rounded border border-slate-200 bg-white px-1 text-center text-xs tabular-nums text-slate-800 outline-none focus:border-blue-400"
      />
      <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
        px
      </span>
    </div>
  );
}

/** Préfixe des liens internes « page du site » (cf. sanitizer + resolveCmsPageHrefs). */
const CMS_PAGE_LINK_PREFIX = "cms-page:";

/**
 * Éditeur de lien du RichText : au choix une URL externe OU une page du site
 * (sélecteur de pages → jeton stable `cms-page:<id>` résolu en href localisé à
 * l'affichage, comme les liens de bouton). Détecte le mode d'après le lien courant.
 */
function LinkEditor({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose: () => void;
}) {
  const cmsNew = useCmsNew();
  const initialHref = (editor.getAttributes("link").href as string) ?? "";
  const isPageInit = initialHref.startsWith(CMS_PAGE_LINK_PREFIX);
  const [mode, setMode] = useState<"url" | "page">(isPageInit ? "page" : "url");
  const [url, setUrl] = useState(isPageInit ? "" : initialHref);
  const [pageId, setPageId] = useState(
    isPageInit ? initialHref.slice(CMS_PAGE_LINK_PREFIX.length) : "",
  );
  const [openInNew, setOpenInNew] = useState(
    (editor.getAttributes("link").target as string | undefined) === "_blank",
  );

  const { data: pages = [] } = useQuery({
    queryKey: [
      "cms-puck",
      "pages",
      {
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
      },
    ],
    queryFn: () =>
      listPuckPages({
        excludeCatalogFlow: true,
        excludeMemberFlow: true,
        excludeShellFlow: true,
      }),
  });

  const apply = () => {
    const chain = editor.chain().focus().extendMarkRange("link");
    // `target`/`rel` par lien selon la case « nouvel onglet ». null = même onglet
    // (le sanitizer API n'ajoute `rel` que si `target="_blank"`).
    const tabAttrs = openInNew
      ? { target: "_blank", rel: "noopener noreferrer" }
      : { target: null, rel: null };
    if (mode === "page" && pageId) {
      chain
        .setLink({ href: `${CMS_PAGE_LINK_PREFIX}${pageId}`, ...tabAttrs })
        .run();
    } else if (mode === "url" && url.trim()) {
      chain.setLink({ href: url.trim(), ...tabAttrs }).run();
    } else {
      chain.unsetLink().run();
    }
    onClose();
  };

  const tabClass = (active: boolean) =>
    cn(
      "flex-1 rounded px-2 py-1 text-xs font-medium transition-colors",
      active
        ? "bg-slate-900 text-white"
        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-100",
    );

  return (
    <div className="flex flex-col gap-2 rounded-t-md border border-slate-200 bg-slate-50 p-2">
      <div className="flex gap-1">
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setMode("url")}
          className={tabClass(mode === "url")}
        >
          {cmsNew("cms.new.fields.richText.linkUrl", "URL")}
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setMode("page")}
          className={tabClass(mode === "page")}
        >
          {cmsNew("cms.new.fields.richText.linkPage", "Page du site")}
        </button>
      </div>
      {mode === "url" ? (
        <input
          autoFocus
          value={url}
          placeholder={cmsNew("cms.new.auto.placeholder.https", "https://…")}
          onKeyDown={(e) => {
            if (e.key === "Enter") apply();
            if (e.key === "Escape") onClose();
          }}
          onChange={(e) => setUrl(e.target.value)}
          className="h-8 w-full min-w-0 rounded border border-slate-200 px-2 text-sm outline-none focus:border-blue-400"
        />
      ) : (
        <select
          autoFocus
          value={pageId}
          onChange={(e) => setPageId(e.target.value)}
          className="h-8 w-full min-w-0 rounded border border-slate-200 bg-white px-2 text-sm outline-none focus:border-blue-400"
        >
          <option value="">
            {cmsNew(
              "cms.new.auto.placeholder.choisir_une_page",
              "Choisir une page…",
            )}
          </option>
          {pages.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name ||
                cmsNew("cms.new.fields.linkTarget.noName", "(sans nom)")}
              {p.status !== "PUBLISHED"
                ? cmsNew("cms.new.fields.linkTarget.draftSuffix", " — brouillon")
                : ""}
            </option>
          ))}
        </select>
      )}
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={openInNew}
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => setOpenInNew(e.target.checked)}
          className="h-3.5 w-3.5"
        />
        {cmsNew(
          "cms.new.fields.richText.linkNewTab",
          "Ouvrir dans un nouvel onglet",
        )}
      </label>
      <div className="flex items-center justify-end gap-1">
        <Btn
          label={cmsNew("cms.new.auto.label.valider_le_lien", "Valider le lien")}
          onClick={apply}
        >
          <Check className="h-4 w-4" />
        </Btn>
        <Btn
          label={cmsNew("cms.new.auto.label.annuler", "Annuler")}
          onClick={onClose}
        >
          <XIcon className="h-4 w-4" />
        </Btn>
      </div>
    </div>
  );
}

function Toolbar({
  editor,
  features,
  baseFontSizePx,
}: {
  editor: Editor;
  features: RichTextFeature[];
  baseFontSizePx: number;
}) {
  const cmsNew = useCmsNew();
  const [linkOpen, setLinkOpen] = useState(false);

  const barClass =
    "flex flex-wrap items-center gap-0.5 rounded-t-md border border-slate-200 bg-slate-50 p-1";

  if (linkOpen) {
    return <LinkEditor editor={editor} onClose={() => setLinkOpen(false)} />;
  }

  const has = (f: RichTextFeature) => features.includes(f);

  return (
    <div className={barClass} data-puck-rt-toolbar="">
      {has("bold") && (
        <Btn
          label={cmsNew("cms.new.auto.label.gras", "Gras")}
          active={markActiveInDoc(editor, "bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <BoldIcon className="h-4 w-4" />
        </Btn>
      )}
      {has("italic") && (
        <Btn
          label={cmsNew("cms.new.auto.label.italique", "Italique")}
          active={markActiveInDoc(editor, "italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <ItalicIcon className="h-4 w-4" />
        </Btn>
      )}
      {has("underline") && (
        <Btn
          label={cmsNew("cms.new.auto.label.souligne", "Souligné")}
          active={markActiveInDoc(editor, "underline")}
          onClick={() => editor.chain().focus().toggleMark("underline").run()}
        >
          <Underline className="h-4 w-4" />
        </Btn>
      )}
      {has("strike") && (
        <Btn
          label={cmsNew("cms.new.auto.label.barre", "Barré")}
          active={markActiveInDoc(editor, "strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-4 w-4" />
        </Btn>
      )}
      {has("color") && (
        <>
          <span className="mx-0.5 h-5 w-px bg-slate-200" />
          <ColorPickerBtn editor={editor} />
        </>
      )}
      {has("fontSize") && (
        <>
          <span className="mx-0.5 h-5 w-px bg-slate-200" />
          <FontSizeBtn editor={editor} baseFontSizePx={baseFontSizePx} />
        </>
      )}
      {has("link") && (
        <>
          <span className="mx-0.5 h-5 w-px bg-slate-200" />
          <Btn
            label={cmsNew("cms.new.auto.label.lien", "Lien")}
            active={markActiveInDoc(editor, "link")}
            onClick={() => setLinkOpen(true)}
          >
            <LinkIcon className="h-4 w-4" />
          </Btn>
        </>
      )}
      {has("lists") && (
        <>
          <span className="mx-0.5 h-5 w-px bg-slate-200" />
          <Btn
            label={cmsNew("cms.new.auto.label.liste_a_puces", "Liste à puces")}
            active={editor.isActive("bulletList")}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <List className="h-4 w-4" />
          </Btn>
          <Btn
            label={cmsNew("cms.new.auto.label.liste_numerotee", "Liste numérotée")}
            active={editor.isActive("orderedList")}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered className="h-4 w-4" />
          </Btn>
        </>
      )}
      {has("heading") && (
        <>
          <span className="mx-0.5 h-5 w-px bg-slate-200" />
          <Btn
            label={cmsNew("cms.new.auto.label.titre_h2", "Titre H2")}
            active={editor.isActive("heading", { level: 2 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
          >
            <Heading2 className="h-4 w-4" />
          </Btn>
          <Btn
            label={cmsNew("cms.new.auto.label.titre_h3", "Titre H3")}
            active={editor.isActive("heading", { level: 3 })}
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
          >
            <Heading3 className="h-4 w-4" />
          </Btn>
        </>
      )}
      {has("align") && (
        <>
          <span className="mx-0.5 h-5 w-px bg-slate-200" />
          <Btn
            label={cmsNew("cms.new.auto.label.aligner_a_gauche", "Aligner à gauche")}
            active={editor.isActive({ textAlign: "left" })}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <AlignLeft className="h-4 w-4" />
          </Btn>
          <Btn
            label={cmsNew("cms.new.auto.label.centrer", "Centrer")}
            active={editor.isActive({ textAlign: "center" })}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <AlignCenter className="h-4 w-4" />
          </Btn>
          <Btn
            label={cmsNew("cms.new.auto.label.aligner_a_droite", "Aligner à droite")}
            active={editor.isActive({ textAlign: "right" })}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <AlignRight className="h-4 w-4" />
          </Btn>
          <Btn
            label={cmsNew(
              "cms.new.auto.label.justifier",
              "Justifier (étaler sur la ligne)",
            )}
            active={editor.isActive({ textAlign: "justify" })}
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          >
            <AlignJustify className="h-4 w-4" />
          </Btn>
        </>
      )}
      {has("paragraphSpacing") && (
        <>
          <span className="mx-0.5 h-5 w-px bg-slate-200" />
          <ParagraphSpacingInput editor={editor} />
        </>
      )}
    </div>
  );
}

export function PuckRichTextEditor({
  value,
  onChange,
  features = ALL_FEATURES,
  placeholder,
  baseFontSizePx = DEFAULT_BASE_FONT_SIZE_PX,
}: {
  value: string | undefined;
  onChange: (html: string) => void;
  features?: RichTextFeature[];
  placeholder?: string;
  /**
   * Taille que reçoit un paragraphe sans taille explicite (celle du bloc posée dans
   * l'onglet Design, sinon 16 px) : valeur affichée par défaut du bouton « Taille du texte ».
   */
  baseFontSizePx?: number;
}) {
  const cmsNew = useCmsNew();
  const [mode, setMode] = useState<"visual" | "code">("visual");
  const [codeHtml, setCodeHtml] = useState(value || EMPTY_RICHTEXT_DOC);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  const editor = useEditor({
    immediatelyRender: false,
    extensions: createPuckRichTextExtensions({
      placeholder:
        placeholder ??
        cmsNew("cms.new.fields.richText.placeholder", "Écrivez votre texte…"),
    }),
    content: value || EMPTY_RICHTEXT_DOC,
    editorProps: {
      attributes: {
        class:
          "cms-html-content max-w-none min-h-[140px] rounded-b-md border border-t-0 border-slate-200 bg-white p-3 text-sm font-normal leading-relaxed outline-none focus:border-blue-400",
      },
      handleTextInput: (view, from, to, text) =>
        handleRichTextInput(view, from, to, text),
    },
    onUpdate({ editor: ed }) {
      // En mode Code, la source de vérité est le textarea — pas TipTap.
      if (modeRef.current === "code") return;
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor || mode === "code") return;
    const incoming = value || EMPTY_RICHTEXT_DOC;
    // Pendant l'édition d'un contrôle toolbar (ex. espacement px), TipTap n'est
    // plus focused : on ne doit pas setContent et perdre la sélection / le draft.
    const toolbarActive = Boolean(
      document.activeElement?.closest?.("[data-puck-rt-toolbar]"),
    );
    if (
      incoming !== editor.getHTML() &&
      !editor.isFocused &&
      !toolbarActive
    ) {
      editor.commands.setContent(incoming, false);
      // Légendes collées avec `<br>` : les transformer pour que l'espacement
      // inter-lignes (margin-bottom) soit visible sans toucher l'input px.
      if (normalizeHardBreaksToParagraphs(editor)) {
        onChange(editor.getHTML());
      }
    }
  }, [value, editor, mode, onChange]);

  // Premier montage : même normalisation si le HTML initial contient des soft breaks.
  useEffect(() => {
    if (!editor || mode === "code") return;
    if (!normalizeHardBreaksToParagraphs(editor)) return;
    onChange(editor.getHTML());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- une seule fois à la création de l'éditeur
  }, [editor]);

  // Sync du textarea si la valeur change de l'extérieur (ex. autre langue) en mode Code.
  useEffect(() => {
    if (mode !== "code") return;
    setCodeHtml(value || EMPTY_RICHTEXT_DOC);
  }, [value, mode]);

  const enterCodeMode = () => {
    if (mode === "code") return;
    const html = editor?.getHTML() ?? value ?? EMPTY_RICHTEXT_DOC;
    setCodeHtml(html);
    setMode("code");
  };

  const enterVisualMode = () => {
    if (mode === "visual") return;
    const html = codeHtml.trim() ? codeHtml : EMPTY_RICHTEXT_DOC;
    editor?.commands.setContent(html, false);
    if (editor && normalizeHardBreaksToParagraphs(editor)) {
      onChange(editor.getHTML());
    } else {
      onChange(html);
    }
    setMode("visual");
  };

  if (!editor) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-end gap-2">
        <span
          className={cn(
            "text-xs font-medium",
            mode === "visual" ? "text-slate-900" : "text-slate-400",
          )}
        >
          {cmsNew("cms.new.fields.richText.modeVisual", "Visuel")}
        </span>
        <Switch
          checked={mode === "code"}
          onCheckedChange={(checked) => {
            if (checked) enterCodeMode();
            else enterVisualMode();
          }}
          aria-label={cmsNew(
            "cms.new.fields.richText.modeCodeSwitch",
            "Basculer en éditeur HTML",
          )}
        />
        <span
          className={cn(
            "text-xs font-medium",
            mode === "code" ? "text-slate-900" : "text-slate-400",
          )}
        >
          {cmsNew("cms.new.fields.richText.modeCode", "Code")}
        </span>
      </div>

      {mode === "code" ? (
        <Textarea
          value={codeHtml}
          onChange={(e) => {
            const next = e.target.value;
            setCodeHtml(next);
            onChange(next);
          }}
          spellCheck={false}
          placeholder={cmsNew(
            "cms.new.fields.richText.codePlaceholder",
            "<p>Votre HTML…</p>",
          )}
          className="min-h-[180px] resize-y font-mono text-xs leading-relaxed"
          aria-label={cmsNew(
            "cms.new.fields.richText.modeCode",
            "Éditeur HTML",
          )}
        />
      ) : (
        <div className="overflow-hidden rounded-md">
          <Toolbar
            editor={editor}
            features={features}
            baseFontSizePx={baseFontSizePx}
          />
          <EditorContent editor={editor} />
        </div>
      )}
    </div>
  );
}

/**
 * Variante pour un bloc dont le « traitement de texte » (onglet Design) fixe la taille
 * de base : le bouton « Taille du texte » affiche cette taille pour un texte sans taille
 * explicite. Lit le style du bloc SÉLECTIONNÉ (le champ est édité dans sa sidebar).
 */
export function PuckBlockRichTextEditor(
  props: Omit<React.ComponentProps<typeof PuckRichTextEditor>, "baseFontSizePx">,
) {
  const { selectedItem } = usePuck();
  const style = (selectedItem?.props as { style?: BlockStyle } | undefined)
    ?.style;
  const designPx = style?.design?.text?.fontSize;
  return (
    <PuckRichTextEditor
      {...props}
      baseFontSizePx={
        typeof designPx === "number" && designPx > 0
          ? designPx
          : DEFAULT_BASE_FONT_SIZE_PX
      }
    />
  );
}
